/**
 * Mobile Menu Tabs Component
 * Allows switching between multiple menus in mobile navigation
 */

if (!customElements.get('mobile-menu-tabs')) {
  customElements.define(
    'mobile-menu-tabs',
    class MobileMenuTabs extends HTMLElement {
      constructor() {
        super();
        this.tabs = [];
        this.activeTabIndex = 0;
        this.currentParentMenu = null;

        // Bind methods
        this.handleTabClick = this.handleTabClick.bind(this);
        this.handleMenuNavigation = this.handleMenuNavigation.bind(this);
      }

      connectedCallback() {
        this.init();
        
        // Listen for navigation events from mobile-menu-slider
        document.addEventListener('mobile-menu:navigate', this.handleMenuNavigation);
      }

      disconnectedCallback() {
        this.tabs.forEach((tab, index) => {
          tab.removeEventListener('click', () => this.handleTabClick(index));
        });
        document.removeEventListener('mobile-menu:navigate', this.handleMenuNavigation);
      }

      handleMenuNavigation(event) {
        const { parentTitle, blockId } = event.detail;
        
        // Check if this navigation event is for the currently active menu
        const activeTab = this.tabs[this.activeTabIndex];
        if (activeTab && activeTab.dataset.blockId === blockId) {
          this.currentParentMenu = parentTitle;
          this.updatePromoBanners();
        }
      }

      init() {
        this.tabs = Array.from(this.querySelectorAll('.mobile-menu-tabs__tab'));
        if (this.tabs.length === 0) return;

        // Add click listeners to tabs
        this.tabs.forEach((tab, index) => {
          tab.addEventListener('click', () => this.handleTabClick(index));
        });

        // Show the first menu by default
        this.showMenu(0);
      }

      handleTabClick(index) {
        if (index === this.activeTabIndex) return;

        // Update tab states
        this.tabs.forEach((tab, i) => {
          if (i === index) {
            tab.classList.add('is-active');
          } else {
            tab.classList.remove('is-active');
          }
        });

        // Reset parent menu when switching tabs
        this.currentParentMenu = null;

        // Update active tab index BEFORE calling showMenu
        // so updatePromoBanners uses the correct index
        this.activeTabIndex = index;

        // Show the corresponding menu
        this.showMenu(index);
      }

      showMenu(index) {
        const tab = this.tabs[index];
        if (!tab) return;

        const blockId = tab.dataset.blockId;
        const menuId = tab.dataset.menuTab;

        // Find all menu sliders
        const allMenus = document.querySelectorAll('mobile-menu-slider[data-block-id]');

        allMenus.forEach((menu) => {
          // Find the parent drawer__block container
          const drawerBlock = menu.closest('.drawer__block');

          if (menu.dataset.blockId === blockId) {
            menu.classList.remove('mobile-menu-slider--hidden');
            if (drawerBlock) {
              drawerBlock.style.display = '';
            }
            // Reset the slider to main panel if it was navigated
            if (typeof menu.resetToMain === 'function') {
              menu.resetToMain();
            }
          } else {
            menu.classList.add('mobile-menu-slider--hidden');
            if (drawerBlock) {
              drawerBlock.style.display = 'none';
            }
          }
        });

        // Update promo banners
        this.updatePromoBanners();
      }

      updatePromoBanners() {
        const allPromoBanners = document.querySelectorAll('.mobile-promo-banner[data-promo-for-menu]');
        const currentMenuNumber = (this.activeTabIndex + 1).toString();
        
        allPromoBanners.forEach((banner) => {
          const targetMenu = banner.dataset.promoForMenu;
          const targetParentLink = banner.dataset.promoParentLink;
          const drawerBlock = banner.closest('.drawer__block');
          
          if (!drawerBlock) return;
          
          // Check menu match
          const menuMatches = !targetMenu || targetMenu === 'all' || targetMenu === currentMenuNumber;
          
          // Check parent link match
          let parentMatches = true;
          if (targetParentLink && targetParentLink.trim() !== '') {
            // If a parent link is specified, check if it matches the current parent menu
            if (this.currentParentMenu) {
              const normalizedCurrent = this.currentParentMenu.toLowerCase().trim();
              const normalizedTarget = targetParentLink.toLowerCase().trim();
              parentMatches = normalizedCurrent === normalizedTarget;
            } else {
              // We're on the main menu, not inside a parent - don't show parent-specific promos
              parentMatches = false;
            }
          } else {
            // No parent link specified - show on main menu only if we're on main menu
            parentMatches = !this.currentParentMenu;
          }
          
          // Show banner only if both menu and parent match
          if (menuMatches && parentMatches) {
            drawerBlock.style.display = '';
          } else {
            drawerBlock.style.display = 'none';
          }
        });
      }
    }
  );
}
